/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useEffect, useMemo, useState } from 'react';

import {
    GetPerformanceDiscountDefinitionsOutput,
    PerformanceDiscounts
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';

import { isEmpty, isUndefined } from 'lodash';
import { contractConfigurationClient } from 'src/api/client';
import { ContractTermInfoButton } from 'src/components/ContractTermInfoButton';
import { Toggle } from 'src/components/Toggle';
import { ContractTermName } from 'src/constants/contractTermHelpInfo';
import { UMG_PERF_DISCOUNT_TYPE_NAMES } from 'src/constants/rateCards';
import { AppContext } from 'src/contexts/appContext';
import { IContractTermsComponentProps, IUmgPerfDiscountRowData } from 'src/types/IRateCard';

export const UmgPerformanceDiscounts = ({
    contractTerms,
    isEditMode,
    handleContractTermValueChange
}: IContractTermsComponentProps) => {
    const { setTopAlertMsg } = React.useContext(AppContext);
    const [performanceDiscounts, setPerformanceDiscounts] = useState(
        contractTerms.performanceDiscounts as PerformanceDiscounts
    );

    useEffect(() => {
        if (isEditMode) {
            contractConfigurationClient.getPerformanceDiscountDefinitions().subscribe(
                (response: GetPerformanceDiscountDefinitionsOutput) => {
                    if (response.performanceDiscounts) {
                        setPerformanceDiscounts(response.performanceDiscounts);
                    }
                },
                (error: UnknownCoralError) => {
                    console.error(error.receivedException?.message);
                    setTopAlertMsg(error.receivedException?.message);
                }
            );
        }
    }, []);

    const discountRowData = useMemo(() => {
        if (!performanceDiscounts) return [];

        const discountData = performanceDiscounts!.sort((discount1, discount2) =>
            discount1!.adjustmentModelName!.value!.localeCompare(discount2!.adjustmentModelName!.value!)
        );

        return discountData.map(
            (discount) =>
                ({
                    threshold: discount.thresholds?.[0].value,
                    discount: discount.discountPercents?.[0].value,
                    discountPercentsWithoutPrimeOrAMF: discount.discountPercentsWithoutPrimeOrAMF?.[0].value
                }) as IUmgPerfDiscountRowData
        );
    }, [performanceDiscounts]) as IUmgPerfDiscountRowData[];

    const handleHasPerformanceDiscountChange = (newVal: boolean) => {
        handleContractTermValueChange('performanceDiscounts', '', newVal ? performanceDiscounts : []);

        /* 
            If UMG Perf Disc is being toggled on AND Growth Target is already enabled,
            toggle off the Growth Target
        */
        if (newVal && !isUndefined(contractTerms.flatDiscount)) {
            handleContractTermValueChange('flatDiscount', '', undefined);
        }
    };

    const tableBackgroundColor = !isEmpty(contractTerms.performanceDiscounts) ? 'principal' : 'highlight';

    return !isEmpty(contractTerms.performanceDiscounts) || isEditMode ? (
        <Box className="box-border-light-gray" type="outline" spacingInset="400">
            <Column>
                <Row spacing="100" alignmentHorizontal="justify">
                    <Row spacing="none">
                        <Heading level={3}>UMG performance discounts</Heading>
                        <ContractTermInfoButton contractTermName={ContractTermName.UMGPerformanceDiscount} />
                    </Row>
                    {isEditMode && (
                        <Toggle
                            label={''}
                            value={!isEmpty(contractTerms.performanceDiscounts)}
                            isEditMode={isEditMode}
                            onChange={handleHasPerformanceDiscountChange}
                        />
                    )}
                </Row>
                {!isEmpty(performanceDiscounts) && (
                    <Table headerRows={1} showDividers={true}>
                        <TableRow>
                            <TableCell>Discount type</TableCell>
                            <TableCell>Threshold</TableCell>
                            <TableCell>Discount</TableCell>
                            <TableCell>Discount if either Prime or AMF are not launched</TableCell>
                        </TableRow>
                        {discountRowData.map((row, index) => (
                            <TableRow key={index}>
                                <TableCell backgroundColor={tableBackgroundColor}>
                                    {UMG_PERF_DISCOUNT_TYPE_NAMES[index]}
                                </TableCell>
                                <TableCell backgroundColor={tableBackgroundColor}>{row.threshold}</TableCell>
                                <TableCell backgroundColor={tableBackgroundColor}>{row.discount}%</TableCell>
                                <TableCell backgroundColor={tableBackgroundColor}>
                                    {row.discountPercentsWithoutPrimeOrAMF
                                        ? row.discountPercentsWithoutPrimeOrAMF + '%'
                                        : ''}
                                </TableCell>
                            </TableRow>
                        ))}
                    </Table>
                )}
            </Column>
        </Box>
    ) : (
        <React.Fragment />
    );
};
